/**
 * Global fetch interceptor for master-admin impersonation 403 detection.
 *
 * Patches window.fetch once so ALL same-origin 403 responses containing the
 * impersonation-block phrase dispatch "master-admin-impersonation-blocked",
 * even from admin-panel pages that use raw fetch() instead of apiRequest.
 *
 * The check in throwIfResNotOk in queryClient.ts covers the apiRequest path;
 * this module ensures raw-fetch callers are also covered.
 */

import { MASTER_ADMIN_IMPERSONATION_PHRASE } from "@/lib/permission-errors";

let _installed = false;

/**
 * Install the interceptor.  Idempotent — subsequent calls are no-ops.
 * Exported so tests can call it with a controlled window.fetch in place.
 */
export function installFetchImpersonationGuard(): void {
  if (typeof window === "undefined" || _installed) return;
  _installed = true;

  const _originalFetch = window.fetch;
  window.fetch = async function guardedFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const response = await _originalFetch.call(this, input, init);
    if (response.status === 403) {
      // Clone before the caller reads the body; fire-and-forget so we do not
      // add latency to the caller's critical path.
      response
        .clone()
        .json()
        .then((data: unknown) => {
          if (
            data &&
            typeof (data as Record<string, unknown>).message === "string" &&
            ((data as Record<string, unknown>).message as string).includes(
              MASTER_ADMIN_IMPERSONATION_PHRASE,
            )
          ) {
            window.dispatchEvent(
              new CustomEvent("master-admin-impersonation-blocked"),
            );
          }
        })
        .catch(() => {
          /* non-JSON or network error — ignore */
        });
    }
    return response;
  };
}

/** Test-only: reset the install guard so tests can call installFetchImpersonationGuard() fresh. */
export function _testOnly_resetInstallGuard(): void {
  _installed = false;
}
